<template>
  <div>
    <b-card class="planner-list-card">
      <div class="gradient-line-wrapper"></div>

      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 resource-title">Planner Replication</h5>
          <small class="text-muted">Manage source and destination planner replication rules</small>
        </div>
      </div>

      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-2">
        <div class="d-flex align-items-center gap-2 flex-wrap">
          <div class="page-size-wrapper">
            <label class="page-size-label">Show:</label>
            <v-select
              v-model="perPage"
              :options="pageSizeOptions"
              :reduce="(option) => option.value"
              label="label"
              :clearable="false"
              :searchable="false"
              append-to-body
              class="page-size-select-compact"
              @input="onPageSizeChange"
            />
          </div>
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to
            <strong>{{ displayedRange.end }}</strong> of
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @input="onPageChange"
          />
        </div>
      </div>

      <b-row class="align-items-center filter-row replication-planner-filters mb-2">
        <b-col cols="12" class="filter-toolbar d-flex align-items-center">
          <div class="filter-fields-group filter-fields-group--cols-4 d-flex align-items-center flex-grow-1">
            <v-select
              v-model="filters.campusID"
              :options="campuses"
              :clearable="true"
              label="branch"
              :reduce="(val) => val.id"
              placeholder="Campus"
              class="filter-control filter-field-item replication-planner-filter-select planner-filter-select"
              @input="onFilterCampusChange($event)"
            />
            <v-select
              v-model="filters.classID"
              :options="filterClassOptions"
              :clearable="true"
              label="name"
              :reduce="(val) => val.id"
              placeholder="Class"
              class="filter-control filter-field-item replication-planner-filter-select planner-filter-select"
              :loading="filterClassesLoading"
              :disabled="filterClassesLoading || !filters.campusID"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.subjectID"
              :options="filterSubjectOptions"
              :clearable="true"
              label="subject"
              :reduce="(val) => val.id"
              placeholder="Subject"
              class="filter-control filter-field-item replication-planner-filter-select planner-filter-select"
              :loading="filterSubjectsLoading"
              :disabled="filterSubjectsLoading || !filters.campusID"
              @input="onFilterChange"
            />
            <v-select
              v-model="filters.sessionID"
              :options="sessions"
              :clearable="true"
              label="session"
              :reduce="(val) => val.id"
              placeholder="Session"
              class="filter-control filter-field-item replication-planner-filter-select planner-filter-select"
              :loading="sessionsLoading"
              :disabled="sessionsLoading || !filters.campusID"
              @input="onFilterChange"
            />
          </div>

          <div class="filter-actions-group d-flex align-items-center ml-auto">
            <b-button
              v-if="rights.add"
              variant="gradient-primary"
              class="action-btn-filter add-sheet-btn text-nowrap"
              v-ripple.400="'rgba(255, 255, 255, 0.15)'"
              @click="navigateToForm(0)"
            >
              <feather-icon icon="PlusIcon" class="mr-50" />
              <span>Add Replication</span>
            </b-button>
          </div>
        </b-col>
      </b-row>

      <div class="table-scroll-container">
        <div class="table-scrollbar-top" ref="topScrollbar">
          <div class="table-scrollbar-content"></div>
        </div>

        <div class="table-wrapper" ref="tableWrapper" @scroll="syncScroll">
          <b-table
            class="planner-list-table mt-1"
            :items="filteredItems"
            :fields="fields"
            :busy="dataLoading"
            show-empty
            :responsive="false"
            hover
            small
          >
          <template #empty>
            <h3 v-if="!dataLoading" class="mt-1 mb-1 text-center">
              {{ $t("No records found") }}
            </h3>
          </template>

          <template #table-busy>
            <tbody class="table-skeleton-body">
              <tr
                v-for="n in skeletonRowCount"
                :key="'skeleton-' + n"
                class="skeleton-row"
              >
                <td
                  v-for="(field, index) in fields"
                  :key="'skeleton-cell-' + index"
                  class="skeleton-cell"
                >
                  <div class="skeleton-loader"></div>
                </td>
              </tr>
            </tbody>
          </template>

          <template #cell(sno)="{ index }">
            {{ (currentPage - 1) * perPage + index + 1 }}
          </template>

          <template #cell(sourceCampusName)="data">
            <b-badge variant="light-primary">
              {{ getCampusName(data.item.sourceCampusID) }}
            </b-badge>
          </template>

          <template #cell(sourceClassName)="data">
            <b-badge variant="light-primary">{{ data.value }}</b-badge>
          </template>

          <template #cell(sourceSectionName)="data">
            <b-badge variant="light-warning">{{ data.value || "All" }}</b-badge>
          </template>

          <template #cell(sourceSubjectName)="data">
            <b-badge variant="light-success">{{ data.value }}</b-badge>
          </template>

          <template #cell(sourceSessionName)="data">
            <b-badge variant="light-info">
              {{ data.item.sourceSessionName || "—" }}
            </b-badge>
          </template>

          <template #cell(destinationCampusName)="data">
            <b-badge variant="light-primary">
              {{ getCampusName(data.item.destinationCampusID) }}
            </b-badge>
          </template>

          <template #cell(destinationClassName)="data">
            <b-badge variant="light-primary">{{ data.value }}</b-badge>
          </template>

          <template #cell(destinationSectionName)="data">
            <b-badge variant="light-warning">{{ data.value || "All" }}</b-badge>
          </template>

          <template #cell(destinationSubjectName)="data">
            <b-badge variant="light-success">{{ data.value }}</b-badge>
          </template>

          <template #cell(destinationSessionName)="data">
            <b-badge variant="light-info">
              {{ data.item.destinationSessionName || "—" }}
            </b-badge>
          </template>

          <template #head(actions)="data">
            <div class="text-center">
              <span>{{ data.label }}</span>
            </div>
          </template>

          <template #cell(actions)="{ item }">
            <div class="d-flex justify-content-center">
              <b-button
                v-if="rights.edit"
                variant="gradient-primary"
                v-b-tooltip.hover.top
                title="Edit"
                class="action-btn-table mr-1"
                @click="navigateToForm(item.id, item)"
              >
                <feather-icon icon="EditIcon" />
              </b-button>

              <b-button
                v-if="rights.delete"
                variant="outline-danger"
                v-b-tooltip.hover.top
                title="Delete"
                class="action-btn-outline delete-btn"
                @click="deleteReplication(item.id)"
              >
                <feather-icon icon="Trash2Icon" />
              </b-button>
            </div>
          </template>
          </b-table>
        </div>
      </div>
    </b-card>

    <!-- View Replication Modal -->
    <b-modal
      v-model="showViewModal"
      title="Planner Replication Details"
      size="lg"
      centered
      no-close-on-backdrop
      hide-footer
    >
      <div v-if="viewReplicationData">
        <b-row>
          <!-- Source Configuration -->
          <b-col md="6">
            <b-card class="mb-3" border-variant="primary">
              <b-card-header class="pb-1">
                <b-card-title class="h6 mb-0">
                  <feather-icon icon="CopyIcon" class="mr-1" />
                  Source Configuration
                </b-card-title>
              </b-card-header>
              <b-card-body class="pt-2">
                <div class="mb-2">
                  <strong>Campus:</strong> 
                  <span class="text-primary font-weight-bold">
                    {{ getCampusName(viewReplicationData.sourceCampusID) }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>Class:</strong> 
                  <span class="text-info font-weight-bold">
                    {{ viewReplicationData.sourceClassName || 'N/A' }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>Section:</strong> 
                  <span class="text-warning font-weight-bold">
                    {{ viewReplicationData.sourceSectionName || 'All' }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>Subject:</strong> 
                  <span class="text-success font-weight-bold">
                    {{ viewReplicationData.sourceSubjectName || 'N/A' }}
                  </span>
                </div>
              </b-card-body>
            </b-card>
          </b-col>

          <!-- Destination Configuration -->
          <b-col md="6">
            <b-card class="mb-3" border-variant="info">
              <b-card-header class="pb-1">
                <b-card-title class="h6 mb-0">
                  <feather-icon icon="ArrowRightIcon" class="mr-1" />
                  Destination Configuration
                </b-card-title>
              </b-card-header>
              <b-card-body class="pt-2">
                <div class="mb-2">
                  <strong>Campus:</strong> 
                  <span class="text-primary font-weight-bold">
                    {{ getCampusName(viewReplicationData.destinationCampusID) }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>Class:</strong> 
                  <span class="text-info font-weight-bold">
                    {{ viewReplicationData.destinationClassName || 'N/A' }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>Section:</strong> 
                  <span class="text-warning font-weight-bold">
                    {{ viewReplicationData.destinationSectionName || 'All' }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>Subject:</strong> 
                  <span class="text-success font-weight-bold">
                    {{ viewReplicationData.destinationSubjectName || 'N/A' }}
                  </span>
                </div>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>

        <!-- Additional Information -->
        <b-card border-variant="secondary">
          <b-card-header class="pb-1">
            <b-card-title class="h6 mb-0">
              <feather-icon icon="InfoIcon" class="mr-1" />
              Additional Information
            </b-card-title>
          </b-card-header>
          <b-card-body class="pt-2">
            <b-row>
              <b-col md="6">
                <div class="mb-2">
                  <strong>Status:</strong> 
                  <b-badge :variant="viewReplicationData.isActive ? 'success' : 'danger'" class="ml-1">
                    {{ viewReplicationData.isActive ? 'Active' : 'Inactive' }}
                  </b-badge>
                </div>
                <div class="mb-2">
                  <strong>Added By:</strong> 
                  <span class="text-muted">{{ viewReplicationData.addedBy || 'N/A' }}</span>
                </div>
              </b-col>
              <b-col md="6">
                <div class="mb-2">
                  <strong>Added On:</strong> 
                  <span class="text-muted">
                    {{ viewReplicationData.addedOn ? new Date(viewReplicationData.addedOn).toLocaleDateString() : 'N/A' }}
                  </span>
                </div>
                <div class="mb-2">
                  <strong>Updated On:</strong> 
                  <span class="text-muted">
                    {{ viewReplicationData.updatedOn ? new Date(viewReplicationData.updatedOn).toLocaleDateString() : 'N/A' }}
                  </span>
                </div>
              </b-col>
            </b-row>
          </b-card-body>
        </b-card>
      </div>

      <template #modal-footer>
        <b-button variant="primary" @click="showViewModal = false">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardHeader,
  BCardTitle,
  BCardBody,
  BRow,
  BCol,
  BFormInput,
  BButton,
  BTable,
  BPagination,
  VBTooltip,
  BSpinner,
  BInputGroupPrepend,
  BInputGroup,
  BFormGroup,
  BModal,
  BBadge,
} from "bootstrap-vue";
import vSelect from "vue-select";
import Ripple from "vue-ripple-directive";
import { mapActions } from "vuex";
import plannerFilterDropdownScrollMixin from "./plannerFilterDropdownScroll";

export default {
  mixins: [plannerFilterDropdownScrollMixin],
  components: {
    BCard,
    BCardHeader,
    BCardTitle,
    BCardBody,
    BRow,
    BCol,
    BFormInput,
    BButton,
    BTable,
    BPagination,
    BSpinner,
    BInputGroupPrepend,
    BInputGroup,
    BFormGroup,
    BModal,
    BBadge,
    vSelect,
  },
  directives: {
    "b-tooltip": VBTooltip,
    Ripple,
  },
  async created() {
    // Initialize rights from store
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({
        name: "misc-not-authorized",
      });
    } else {
      this.rights = this.$store.state.rights[this.$route.name];
      // Hit table API first so skeleton shows immediately
      const tablePromise = this.loadData();
      await Promise.all([
        tablePromise,
        this.loadCampuses(),
        this.loadSessions(),
        this.loadClasses(),
        this.loadSubjects(),
      ]);
    }
  },
  mounted() {
    this.$nextTick(() => this.updateScrollbarWidth());
  },
  updated() {
    this.$nextTick(() => this.updateScrollbarWidth());
  },
  data() {
    return {
      rights: {},
      dataLoading: true,
      currentPage: 1,
      perPage: 20,
      totalCount: 0,
      pageSizeOptions: [
        { label: "10", value: 10 },
        { label: "20", value: 20 },
        { label: "50", value: 50 },
        { label: "100", value: 100 },
      ],
      campuses: [],
      sessions: [],
      classes: [],
      subjects: [],
      filterClasses: [],
      filterSubjects: [],
      filterClassesLoading: false,
      filterSubjectsLoading: false,
      sessionsLoading: false,
      filters: {
        campusID: null,
        classID: null,
        subjectID: null,
        sessionID: null,
      },

      fields: [
        { key: "sno", label: "S.No", thClass: "text-center", tdClass: "text-center" },
        { label: "Source Campus", key: "sourceCampusName" },
        { label: "Source Class", key: "sourceClassName" },
        { label: "Source Section", key: "sourceSectionName" },
        { label: "Source Subject", key: "sourceSubjectName" },
        { label: "Source Session", key: "sourceSessionName" },
        { label: "Destination Campus", key: "destinationCampusName" },
        { label: "Destination Class", key: "destinationClassName" },
        { label: "Destination Section", key: "destinationSectionName" },
        { label: "Destination Subject", key: "destinationSubjectName" },
        { label: "Destination Session", key: "destinationSessionName" },
        { key: "actions", label: "Actions", thClass: "text-center", tdClass: "text-center" },
      ],

      items: [],
      showViewModal: false,
      viewReplicationData: null,
    };
  },
  computed: {
    reportDomain() {
      return (this.$store.state.report || "").replace(/_/g, "");
    },
    plannerCampusID() {
      return this.reportDomain === "myskooliuss"
        ? 1
        : this.$store.state.userData.cId;
    },
    filteredItems() {
      return this.items;
    },
    skeletonRowCount() {
      return Math.max(Number(this.perPage) || 10, 8);
    },
    // Filter options for classes based on selected campus
    filterClassOptions() {
      if (!this.filters.campusID) {
        return this.classes; // Show all classes if no campus selected
      }
      return this.filterClasses;
    },
    // Filter options for subjects based on selected campus
    filterSubjectOptions() {
      if (!this.filters.campusID) {
        return this.subjects; // Show all subjects if no campus selected
      }
      return this.filterSubjects;
    },
    paginationInfo() {
      return {
        currentPage: this.currentPage,
        perPage: this.perPage,
        totalCount: this.totalCount,
        totalPages: Math.ceil(this.totalCount / this.perPage),
        showingItems: this.items.length
      };
    },
    displayedRange() {
      if (this.totalCount === 0) {
        return { start: 0, end: 0 };
      }
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
  },
  methods: {
    ...mapActions({ get: "get", post: "post", put: "put", delete: "delete" }),

    async loadCampuses() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "campuses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        this.campuses = await this.get(obj);
        console.log("Loaded campuses:", this.campuses);
      } catch (error) {
        console.error("Error loading campuses:", error);
        this.campuses = [];
      }
    },

    normalizeCampusId(campusID) {
      if (campusID == null || campusID === "") return null;
      const id = Number(campusID);
      return Number.isFinite(id) && id > 0 ? id : null;
    },

    async loadSessions(campusID) {
      const cID =
        this.normalizeCampusId(campusID) ||
        this.normalizeCampusId(this.$store.state.userData.cId);
      if (!cID) {
        this.sessions = [];
        return;
      }

      this.sessionsLoading = true;
      try {
        const obj = {
          url: `${this.$store.state.domain}Sessions?db=${this.$store.state.userData.db}&cID=${cID}`,
          token: this.$store.state.userData.token,
        };
        const data = await this.get(obj);
        this.sessions = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error loading sessions:", error);
        this.sessions = [];
      } finally {
        this.sessionsLoading = false;
      }
    },

    async loadClasses() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const classesData = await this.get(obj);
        this.classes = classesData.map((el) => el.cls);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
      }
    },

    async loadSubjects() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        this.subjects = await this.get(obj);
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.subjects = [];
      }
    },

    async loadFilterClasses(campusID) {
      const cId = this.normalizeCampusId(
        campusID != null ? campusID : this.filters.campusID
      );
      if (!cId) {
        this.filterClasses = [];
        return;
      }

      this.filterClassesLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const list = Array.isArray(response) ? response : [];
        this.filterClasses = list.map((el) => el.cls || el);
      } catch (error) {
        console.error("Error loading filter classes:", error);
        this.filterClasses = [];
      } finally {
        this.filterClassesLoading = false;
      }
    },

    async loadFilterSubjects(campusID) {
      const cId = this.normalizeCampusId(
        campusID != null ? campusID : this.filters.campusID
      );
      if (!cId) {
        this.filterSubjects = [];
        return;
      }

      this.filterSubjectsLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            cId,
          token: this.$store.state.userData.token,
        };
        const data = await this.get(obj);
        this.filterSubjects = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error loading filter subjects:", error);
        this.filterSubjects = [];
      } finally {
        this.filterSubjectsLoading = false;
      }
    },

    getCampusName(campusID) {
      if (!campusID || !this.campuses.length) return 'Unknown Campus';
      const campus = this.campuses.find(c => c.id === campusID);
      return campus ? campus.branch : 'Unknown Campus';
    },

    async loadData() {
      this.dataLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerReplication/GetByPagination?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&page=" +
            this.currentPage +
            "&pageSize=" +
            this.perPage +
            "&filterCampusID=" + (this.filters.campusID || '') +
            "&classID=" + (this.filters.classID || '') +
            "&subjectID=" + (this.filters.subjectID || '') +
            "&sessionID=" + (this.filters.sessionID || ''),
          token: this.$store.state.userData.token,
          dashboard: true,
        };

        console.log("Loading data with pagination:", {
          pageNumber: this.currentPage,
          pageSize: this.perPage,
          url: obj.url
        });

        let res = await this.get(obj);
        console.log("API Response:", res);

        // Handle different response structures
        if (res && res.status === "success" && Array.isArray(res.data)) {
          this.items = res.data;
          this.totalCount = res.total || res.totalCount || res.totalRecords || 0;
          console.log("Loaded items:", this.items.length, "Total count:", this.totalCount);
        } else if (res && Array.isArray(res)) {
          // Handle case where response is directly an array
          this.items = res;
          this.totalCount = res.length;
          console.log("Loaded items (direct array):", this.items.length);
        } else if (res && res.data && Array.isArray(res.data)) {
          // Handle case where data is nested but no status
          this.items = res.data;
          this.totalCount = res.total || res.totalCount || res.totalRecords || res.data.length;
          console.log("Loaded items (nested data):", this.items.length, "Total count:", this.totalCount);
        } else {
          this.items = [];
          this.totalCount = 0;
          console.log("No data found or invalid response format:", res);
        }
      } catch (error) {
        console.error("Error loading data:", error);
        this.items = [];
        this.totalCount = 0;
        this.$bvToast.toast("Error loading planner replications. Please try again.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
        this.$nextTick(() => this.updateScrollbarWidth());
      }
    },

    navigateToForm(id = 0, data = null) {
      if (id === 0) {
        // Add mode
        this.$router.push({
          name: "replication-planner-form",
          params: { id: 0 },
        });
      } else {
        // Edit mode
        this.$router.push({
          name: "replication-planner-form",
          params: { id: id },
        });
      }
    },

    async deleteReplication(id) {
      const result = await this.$swal.fire({
        title: this.$t("Are you sure?"),
        text: this.$t("You won't be able to revert this!"),
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: this.$t("Yes, Delete it!"),
      });

      if (result.isConfirmed) {
        try {
          const obj = {
            url:
              this.$store.state.domain +
              "PlannerReplication/Delete?db=" +
              this.$store.state.userData.db +
              "&campusID=" +
              this.$store.state.userData.cId +
              "&id=" +
              id,
            message: "Planner replication deleted successfully.",
            context: this,
            token: this.$store.state.userData.token,
          };

          const success = await this.delete(obj);
          if (success) {
            this.loadData();
          }
        } catch (error) {
          console.error("Error deleting replication:", error);
          this.$bvToast.toast("Error deleting planner replication. Please try again.", {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
      }
    },

    async viewReplication(id) {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "PlannerReplication/LoadSelected?db=" +
            this.$store.state.userData.db +
            "&campusID=" +
            this.$store.state.userData.cId +
            "&id=" +
            id,
          token: this.$store.state.userData.token,
          returnData: true, // Get the raw response data
        };

        const response = await this.get(obj);
        console.log("View replication data:", response);

        let replication = null;
        
        // Handle different response structures
        if (response && response.status === "success" && response.data && response.data.length > 0) {
          // Response has status and data structure
          replication = response.data[0];
          console.log("Using status.data structure");
        } else if (response && Array.isArray(response) && response.length > 0) {
          // Response is a direct array
          replication = response[0];
          console.log("Using direct array structure");
        } else if (response && response.data && Array.isArray(response.data) && response.data.length > 0) {
          // Response has data array without status
          replication = response.data[0];
          console.log("Using data array structure");
        } else if (response && typeof response === 'object' && response.id) {
          // Response is a direct object
          replication = response;
          console.log("Using direct object structure");
        }

        if (replication) {
          // Store the replication data for the modal
          this.viewReplicationData = replication;
          this.showViewModal = true;
        } else {
          console.error("No valid replication data found in response:", response);
          this.$bvToast.toast("No replication data found.", {
            title: "Not Found",
            variant: "warning",
            solid: true,
          });
        }
      } catch (error) {
        console.error("Error viewing replication:", error);
        this.$bvToast.toast("Error loading replication details. Please try again.", {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      }
    },

    onPageChange(page) {
      console.log("Page changed to:", page);
      this.currentPage = page;
      this.loadData();
    },


    async onFilterChange() {
      this.currentPage = 1; // Reset to first page when filters change
      this.loadData();
    },

    async onFilterCampusChange(campusID) {
      const cId = this.normalizeCampusId(campusID);
      if (cId != null) {
        this.filters.campusID = cId;
      } else {
        this.filters.campusID = null;
      }

      this.filters.classID = null;
      this.filters.subjectID = null;
      this.filters.sessionID = null;
      this.filterClasses = [];
      this.filterSubjects = [];

      if (cId) {
        await Promise.all([
          this.loadFilterClasses(cId),
          this.loadFilterSubjects(cId),
          this.loadSessions(cId),
        ]);
      } else {
        this.sessions = [];
        await this.loadSessions(this.$store.state.userData.cId);
      }

      this.currentPage = 1;
      this.loadData();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadData();
      this.$nextTick(() => this.updateScrollbarWidth());
    },
    syncScroll() {
      if (this.$refs.topScrollbar && this.$refs.tableWrapper) {
        this.$refs.topScrollbar.scrollLeft = this.$refs.tableWrapper.scrollLeft;
      }
    },
    updateScrollbarWidth() {
      const wrapper = this.$refs.tableWrapper;
      const content = this.$el.querySelector(".table-scrollbar-content");
      if (wrapper && content) {
        content.style.width = `${Math.max(wrapper.scrollWidth, wrapper.clientWidth)}px`;
      }
      const top = this.$refs.topScrollbar;
      if (top && !top._bound) {
        top.addEventListener("scroll", () => {
          if (this.$refs.tableWrapper) {
            this.$refs.tableWrapper.scrollLeft = top.scrollLeft;
          }
        });
        top._bound = true;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "./plannerListTable.scss";

.table-scroll-container {
  position: relative;
  width: 100%;
}

.table-wrapper {
  overflow-x: auto;
  width: 100%;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }
}

.table-scrollbar-top {
  overflow-x: auto;
  overflow-y: hidden;
  width: 100%;
  height: 12px;
  margin-bottom: 8px;
  -webkit-overflow-scrolling: touch;
  scrollbar-width: thin;
  scrollbar-color: #cbd5e1 #f1f5f9;

  &::-webkit-scrollbar {
    height: 10px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 6px;

    &:hover {
      background: #94a3b8;
    }
  }

  .table-scrollbar-content {
    height: 1px;
    width: 100%;
  }

  @media (max-width: 768px) {
    display: none;
  }
}

.planner-list-table {
  min-width: 1400px;
}

::v-deep .table-skeleton-body {
  min-height: 360px;
}

::v-deep .skeleton-row {
  height: 52px;
}

::v-deep .skeleton-cell {
  min-width: 110px;
  padding: 1rem 0.875rem !important;
}

::v-deep .skeleton-loader {
  width: 100% !important;
  min-width: 70px;
  height: 20px;
}

</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";

@import "./plannerFilterDropdownMenu.scss";
</style>
